import React, { useState } from "react";
import { Rocket, Loader2, Square, CheckCircle2, Circle, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { usePoll } from "@/hooks/usePoll";

const Icon = ({ s }) => s === "done" ? <CheckCircle2 size={15} className="text-[#34d399]" />
  : s === "running" ? <Loader2 size={15} className="animate-spin text-[#E6B04E]" /> : <Circle size={15} className="text-[#3B4259]" />;

export const ProductionPanel = ({ projectId }) => {
  const { data, setData } = usePoll(() => api.production(projectId), 8000, [projectId]);
  const [busy, setBusy] = useState(false);
  if (!data) return null;
  const p = data.production;
  const running = p?.status === "running";
  const act = async (fn, msg) => { setBusy(true); try { setData(await fn()); toast.success(msg); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); } };
  const pend = Object.values(data.pending_by_type).reduce((a, b) => a + b, 0);

  return (
    <div className="studio-card p-5 space-y-4" data-testid="production-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2"><Rocket size={17} className="text-[#E6B04E]" /><span className="font-head font-bold">Auto Production</span>
            {p && <span className={`chip ${p.status === "completed" ? "chip-KEEP" : p.status === "failed" ? "chip-REJECTED" : running ? "chip-REVIEW" : "chip-UNRATED"}`} data-testid="production-status">{p.status}</span>}</div>
          <p className="text-[#64748B] text-xs mt-1" data-testid="production-media">
            {data.media.ready}/{data.media.total} clips editable (proxy ready) · {pend} job(s) in queue — import → analysis → sync → wedding AI → color → story → render, hands-free
          </p>
        </div>
        {running
          ? <button className="btn btn-ghost" disabled={busy} onClick={() => act(() => api.stopProduction(projectId), "Auto Production paused")} data-testid="btn-production-stop"><Square size={14} />Stop</button>
          : <button className="btn btn-gold" disabled={busy} onClick={() => act(() => api.startProduction(projectId), "Auto Production started")} data-testid="btn-production-start">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Rocket size={14} />}{p ? "Resume production" : "Send to production"}</button>}
      </div>
      <div className="h-1.5 rounded-full bg-[#0c0e14] overflow-hidden"><div className="h-full bg-[#E6B04E] transition-[width]" style={{ width: `${data.media.total ? (data.media.ready / data.media.total) * 100 : 0}%` }} /></div>
      {p && (
        <div className="grid md:grid-cols-4 xl:grid-cols-8 gap-2">
          {data.order.map((o) => {
            const st = p.stages[o.key] || {};
            return (
              <div key={o.key} className="flex items-start gap-2 text-xs" data-testid={`production-stage-${o.key}`}>
                <Icon s={st.status} />
                <div><div className="font-semibold">{o.label}</div>
                  <div className="text-[#64748B]">{st.status === "running" && st.pending != null ? `${st.pending} pending` : st.status}{st.failed_jobs ? ` · ${st.failed_jobs} failed` : ""}</div></div>
              </div>
            );
          })}
        </div>
      )}
      {p?.tune && (
        <div className="text-xs space-y-1" data-testid="production-tune">
          <div className="font-semibold text-[#E6B04E]">Auto-tune results</div>
          {p.tune.changes.length ? p.tune.changes.map((c, i) => <div key={i}>• {c}</div>) : <div>No changes needed — first render passed all checks.</div>}
        </div>
      )}
      {p?.error && <p className="text-xs text-[#f87171] flex gap-1.5" data-testid="production-error"><XCircle size={14} />{p.error}</p>}
    </div>
  );
};
